/*
 * This file is copied and modified from: https://github.com/xdave/typescript-fsa-redux-thunk/blob/5bdd5ee9fe80ac6a6ff5f62b541ea96fb7d0ca82/src/index.ts
 * under the MIT License
 */
import { ThunkDispatch, ThunkAction } from 'redux-thunk'
import { ActionCreatorFactory, AnyAction, AsyncActionCreators, Meta } from 'typescript-fsa'

/**
 * It's either a promise, or it isn't
 */
export type MaybePromise<T> = T | Promise<T>

/**
 * A redux-thunk with the params as the first argument.  You don't have to
 * return a promise; but, the result of the dispatch will be one.
 */
export type AsyncWorker<TParams, TResult, TState, TExtra> = (
    params: TParams,
    dispatch: ThunkDispatch<TState, any, AnyAction>,
    getState: () => TState,
    extra?: TExtra
) => MaybePromise<TResult>

/** Workaround for typescript-fsa issue #77 */
export type ThunkReturnType<T> = T extends void ? unknown : T extends Promise<T> ? Promise<T> : T

export class AsyncActionError {
    constructor(public msg: any) {}
    toString() {
        return `AsyncActionError: ${this.msg}`
    }
}

/**
 * Factory function to easily create a thunk
 *
 * @param create typescript-fsa action creator factory
 * @returns an function that takes
 *  - the `type` of the action,
 *  - your worker thunk function
 *  - And returns object with the async actions and the thunk itself. This object can be called
 *    as a function and the result dispatched to redux.
 */
export const asyncFactory = <TState, TExtra = any>(
    create: ActionCreatorFactory,
    resolve: () => Promise<void> = Promise.resolve.bind(Promise)
) => <TParams, TResult, TError = any>(
    type: string,
    worker: AsyncWorker<TParams, ThunkReturnType<TResult>, TState, TExtra>,
    commonMeta?: Meta
) => {
    // Create the async action creators (started, done, failed)
    const async = create.async<TParams, ThunkReturnType<TResult>, TError>(type, commonMeta)

    // Type of the Thunk action creator
    type Thunk = ThunkFunction<TState, TParams, ThunkReturnType<TResult>, TError, TExtra>

    // Create the Thunk
    const fn: Thunk = (...params: OptionalArgTuple<TParams>) => (dispatch, getState, extra) =>
        resolve()
            .then(() => {
                const param = params[0]
                dispatch(async.started(param!))
            })
            .then(() => worker(params[0]!, dispatch, getState, extra))
            .then((result) => {
                dispatch(async.done({ params: params[0]!, result }))
                return result
            })
            .catch((error) => {
                dispatch(async.failed({ params: params[0]!, error: error.toString() }))
                if (error instanceof AsyncActionError) {
                    throw { response: error.msg }
                }
                throw error
            })
    fn.action = fn
    fn.async = async
    return fn
}

/**
 * Hack to allow Thunk functions to
 */
type OptionalArgTuple<T> = T extends undefined ? [] : [T]

/**
 * Represents a callable Thunk function. It also stores itself in a field `.action`, and
 * the individual started/failed/done async action creators under `.async`.
 */
export interface ThunkFunction<TState, TParams, TResult, TError, TExtra> {
    (...params: OptionalArgTuple<TParams>): (
        dispatch: ThunkDispatch<TState, any, AnyAction>,
        getState: () => TState,
        extra?: TExtra
    ) => Promise<TResult | undefined>
    action(...params: OptionalArgTuple<TParams>): ReturnType<this>
    // tslint:disable-next-line: member-ordering
    async: AsyncActionCreators<TParams, TResult, TError>
}

/** Utility type for a function that takes paras and returns a redux-thunk */
export type ThunkCreator<TParams, TResult, TState> = (
    params?: TParams
) => ThunkAction<Promise<TResult>, TState, any, AnyAction>

/** The result type for thunkToAction below */
export type ThunkFn<TParams, TResult> = (...params: OptionalArgTuple<TParams>) => Promise<TResult>

/**
 * Passing the result of this to bindActionCreators and then calling the result
 * is equivalent to calling `store.dispatch(thunkCreator(params))`. Useful
 * for when you pass it to `connect()` in an action creators map object.
 * @param thunkCreator The thunk action creator
 * @returns thunkAction as if it was bound
 */
export const thunkToAction = <TParams, TResult, TState>(
    thunkCreator: ThunkCreator<TParams, TResult, TState>
): ThunkFn<TParams, TResult> => thunkCreator as any

export default asyncFactory
